import {zodResolver} from '@hookform/resolvers/zod';
import {Badge, Button, Modal, Select, TextInput, Textarea} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconTrash} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import type {
  JobHazardAnalysisStepResponse,
  JobStepMetaDataRequest,
  RiskAssessmentCodeResponse,
} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {z} from 'zod';
import {
  getRiskColor,
  getRiskLabel,
  safetySelectOptions,
} from './jobHazardAnalysisHelpers';

const safetySelectData = safetySelectOptions.map(opt => ({
  label: opt.label,
  value: String(opt.id),
}));

export const initialSafetyObj = {
  id: '',
  safetyControlsTitle: '',
  safetyNotes: '',
};

const requiredMessage = 'This field is required.';

const jobHazardAnalysisStepFormSchema = z.object({
  name: z.string().min(1, requiredMessage),
  potentialHazard: z.string().min(1, requiredMessage),
  riskBeforeProbability: z.number().min(1, requiredMessage),
  riskBeforeSeverity: z.number().min(1, requiredMessage),
  riskAfterProbability: z.number().min(1, requiredMessage),
  riskAfterSeverity: z.number().min(1, requiredMessage),
});

export type JobHazardAnalysisStepFormValues = z.infer<
  typeof jobHazardAnalysisStepFormSchema
>;

const toFormValues = (
  step: JobHazardAnalysisStepResponse
): JobHazardAnalysisStepFormValues => ({
  name: step.name,
  potentialHazard: step.potentialHazard ?? '',
  riskBeforeProbability: step.riskBeforeProbability,
  riskBeforeSeverity: step.riskBeforeSeverity,
  riskAfterProbability: step.riskAfterProbability,
  riskAfterSeverity: step.riskAfterSeverity,
});

export interface JobHazardAnalysisStepModalProps {
  handleClose: () => void;
  open: boolean;
  riskAsset: RiskAssessmentCodeResponse;
  step: JobHazardAnalysisStepResponse;
  onSave: (step: JobHazardAnalysisStepResponse) => void;
}

export const JobHazardAnalysisStepModal: React.FC<
  JobHazardAnalysisStepModalProps
> = ({handleClose, open, step, riskAsset, onSave}) => {
  const [safetyList, setSafetyList] = useState<JobStepMetaDataRequest[]>(
    step.safetyControls
  );
  const [nextSafetyControlId, setNextSafetyControlId] = useState(1);

  const {control, handleSubmit, reset} =
    useForm<JobHazardAnalysisStepFormValues>({
      defaultValues: toFormValues(step),
      resolver: zodResolver(jobHazardAnalysisStepFormSchema),
    });

  useEffect(() => {
    if (!open) return;
    reset(toFormValues(step));
    setSafetyList(step.safetyControls);
    setNextSafetyControlId(1);
  }, [open, step, reset]);

  const probabilityOptions = useMemo(() => {
    return Object.values(riskAsset?.probability || []).map((item, index) => ({
      label: `${index + 1} - ${item}`,
      value: String(index + 1),
    }));
  }, [riskAsset?.probability]);

  const severityOptions = useMemo(() => {
    return Object.values(riskAsset?.severity || []).map((item, index) => ({
      label: `${index + 1} - ${item}`,
      value: String(index + 1),
    }));
  }, [riskAsset?.severity]);

  const riskBeforeProbability = useWatch({
    control,
    name: 'riskBeforeProbability',
  });
  const riskBeforeSeverity = useWatch({control, name: 'riskBeforeSeverity'});
  const riskAfterProbability = useWatch({
    control,
    name: 'riskAfterProbability',
  });
  const riskAfterSeverity = useWatch({control, name: 'riskAfterSeverity'});

  const riskBeforeValue = riskBeforeProbability * riskBeforeSeverity;
  const riskAfterValue = riskAfterProbability * riskAfterSeverity;
  const riskBeforeColor = getRiskColor(riskAsset, riskBeforeValue);
  const riskAfterColor = getRiskColor(riskAsset, riskAfterValue);
  const hasRiskBeforeValue = !!(riskBeforeProbability && riskBeforeSeverity);
  const hasRiskAfterValue = !!(riskAfterProbability && riskAfterSeverity);

  const handleAddSafetyControl = () => {
    setSafetyList(prevState => [
      ...prevState,
      {...initialSafetyObj, id: nextSafetyControlId},
    ]);
    setNextSafetyControlId(nextSafetyControlId + 1);
  };

  const handleRemoveSafetyControl = (id: number) => {
    setSafetyList(safetyList.filter(item => item.id !== id));
  };

  const handleSafetyChange = (name: string, value: string, id: number) => {
    setSafetyList(prevState =>
      prevState.map(obj => (obj.id === id ? {...obj, [name]: value} : obj))
    );
  };

  const handleNotesChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const {name, value} = event.target;
    setSafetyList(prevState =>
      prevState.map(obj => (obj.id === index ? {...obj, [name]: value} : obj))
    );
  };

  const onSubmitStep = (values: JobHazardAnalysisStepFormValues) => {
    onSave({
      ...step,
      ...values,
      racBeforeControls:
        values.riskBeforeProbability * values.riskBeforeSeverity,
      racAfterControls: values.riskAfterProbability * values.riskAfterSeverity,
      safetyControls: safetyList,
    });
  };

  return (
    <Modal onClose={handleClose} opened={open} title="Job Step" size={900}>
      <Stack gap="lg">
        {/* Step Details */}
        <Stack gap="sm">
          <Controller
            name="name"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Job Step"
                placeholder="e.g., Set up scaffolding"
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="potentialHazard"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Potential Hazards"
                placeholder="e.g., Fall from height, electrical shock"
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </Stack>

        <Divider />

        {/* Risk Before Controls */}
        <Stack gap="sm">
          <Title order={5}>Risk Before Controls</Title>
          <SimpleGrid cols={2}>
            <Controller
              name="riskBeforeProbability"
              control={control}
              render={({field}) => (
                <Select
                  label="Probability"
                  placeholder="Select probability level"
                  data={probabilityOptions}
                  value={!field.value ? null : String(field.value)}
                  onChange={value => field.onChange(Number(value))}
                />
              )}
            />
            <Controller
              name="riskBeforeSeverity"
              control={control}
              render={({field}) => (
                <Select
                  label="Severity"
                  placeholder="Select severity level"
                  data={severityOptions}
                  value={!field.value ? null : String(field.value)}
                  onChange={value => field.onChange(Number(value))}
                />
              )}
            />
          </SimpleGrid>
          <Box
            p="sm"
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              backgroundColor: hasRiskBeforeValue
                ? `var(--mantine-color-${riskBeforeColor}-1)`
                : 'var(--mantine-color-gray-1)',
              border: `1px solid var(--mantine-color-${
                hasRiskBeforeValue ? riskBeforeColor : 'gray'
              }-3)`,
              textAlign: 'center',
            }}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Risk Assessment Code
            </Text>
            {hasRiskBeforeValue ? (
              <Badge color={riskBeforeColor} variant="filled" size="lg">
                {getRiskLabel(riskAsset, riskBeforeValue)}
              </Badge>
            ) : (
              <Text c="dimmed" size="sm">
                Select probability and severity
              </Text>
            )}
          </Box>
        </Stack>

        <Divider />

        {/* Safety Measures */}
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={5}>Safety Measures</Title>
            <CreateButton
              size="sm"
              variant="subtle"
              color="yellow"
              onClick={handleAddSafetyControl}
              disabled={safetyList.length === 6}>
              Add Safety Control
            </CreateButton>
          </Group>
          {safetyList?.map((item, index) => (
            <Card key={item.id} withBorder padding="md" bg="gray.0">
              <Group justify="space-between" mb="sm">
                <Text fw={600} size="sm">
                  Safety Control {index + 1}
                </Text>
                <Button
                  size="sm"
                  color="red"
                  variant="subtle"
                  onClick={() => handleRemoveSafetyControl(item.id)}
                  leftSection={<IconTrash size={16} />}
                  disabled={safetyList.length === 1}>
                  Remove
                </Button>
              </Group>
              <Select
                label="Type"
                placeholder="Select a safety control type"
                data={safetySelectData}
                value={item.safetyControlsTitle || null}
                onChange={value =>
                  handleSafetyChange(
                    'safetyControlsTitle',
                    value ?? '',
                    item.id
                  )
                }
                mb="sm"
              />
              <Textarea
                label="Notes"
                name="safetyNotes"
                placeholder="Describe the safety control measures"
                value={item.safetyNotes}
                onChange={e => handleNotesChange(e, item.id)}
                maxLength={2000}
                description={`${item.safetyNotes?.length ?? 0} / 2,000`}
                autosize
                minRows={3}
              />
            </Card>
          ))}
        </Stack>

        <Divider />

        {/* Risk After Controls */}
        <Stack gap="sm">
          <Title order={5}>Risk After Controls</Title>
          <SimpleGrid cols={2}>
            <Controller
              name="riskAfterProbability"
              control={control}
              render={({field}) => (
                <Select
                  label="Probability"
                  placeholder="Select probability level"
                  data={probabilityOptions}
                  value={!field.value ? null : String(field.value)}
                  onChange={value => field.onChange(Number(value))}
                />
              )}
            />
            <Controller
              name="riskAfterSeverity"
              control={control}
              render={({field}) => (
                <Select
                  label="Severity"
                  placeholder="Select severity level"
                  data={severityOptions}
                  value={!field.value ? null : String(field.value)}
                  onChange={value => field.onChange(Number(value))}
                />
              )}
            />
          </SimpleGrid>
          <Box
            p="sm"
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              backgroundColor: hasRiskAfterValue
                ? `var(--mantine-color-${riskAfterColor}-1)`
                : 'var(--mantine-color-gray-1)',
              border: `1px solid var(--mantine-color-${
                hasRiskAfterValue ? riskAfterColor : 'gray'
              }-3)`,
              textAlign: 'center',
            }}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Risk Assessment Code
            </Text>
            {hasRiskAfterValue ? (
              <Badge color={riskAfterColor} variant="filled" size="lg">
                {getRiskLabel(riskAsset, riskAfterValue)}
              </Badge>
            ) : (
              <Text c="dimmed" size="sm">
                Select probability and severity
              </Text>
            )}
          </Box>
        </Stack>
      </Stack>

      <Group mt="md" justify="flex-start">
        <Button color="gray" variant="subtle" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="yellow"
          variant="filled"
          onClick={handleSubmit(onSubmitStep)}>
          Save Step
        </Button>
      </Group>
    </Modal>
  );
};
